import { parseKeyDate } from './download-grouping.helper';
import { ExportStatusClass } from './export-status.helper';

/**
 * What a deel the group expects but the list does not hold actually is. The two look identical in the
 * response — an absent part number — and they are opposites to the reader: one is still coming, the other
 * will never come. Calling every gap `gone` is what made a running export announce its own loss.
 */
export type GapKind = 'pending' | 'gone';

/** How long after its aanmaakmoment a group is still taken to be making delen. */
export const PENDING_WINDOW: number = 30 * 60 * 1000;

export interface GapContext {
    readonly key: string;
    /** The delen that *are* in the list. Their status is the clearest evidence about the ones that are not. */
    readonly statuses: readonly ExportStatusClass[];
    /** Whether any deel present has been cleaned up, which is what happens to a whole export at once. */
    readonly expired: boolean;
    /** Whether the page holds the full list. A truncated one is missing delen it was never told about. */
    readonly complete: boolean;
    readonly now?: number;
}

/**
 * Four rungs, hardest evidence first. The backend cannot serve a deel it has not made yet, so nothing here
 * proves a gap is pending; each rung rules out an innocent explanation until only loss is left.
 */
export function classifyGap(context: GapContext): GapKind {
    // We did not fetch the whole list, so this gap may well be sitting in the part we did not ask for.
    if (!context.complete) {
        return 'pending';
    }

    // A deel still running means the export is still being made, and the rest of it with it.
    if (context.statuses.some(status => status === 'open')) {
        return 'pending';
    }

    // An export expires whole. If what survives has been cleaned up, what is absent was cleaned up too.
    if (context.expired) {
        return 'gone';
    }

    // Nothing left to read but the clock: young enough to still be working, or old enough to have stopped.
    return withinWindow(context.key, context.now ?? Date.now()) ? 'pending' : 'gone';
}

function withinWindow(key: string, now: number): boolean {
    const created: number | null = parseKeyDate(key);

    return created !== null && now - created < PENDING_WINDOW;
}
